// Phase 1.3 (hh-y7i) — HistoryScreen.
// Chat-view container: a scrollable list of messages from the assistant and the
// user, opened from the bottom-nav (or the FAB menu). This is a pure UI
// container — it receives the feed as a prop; fetching the real chat history
// from the DB lands in hh-46n (wire-up).
//
// Ported (intent only) from designproject/files/screens-feed.jsx
// (`HistoryScreen` + `Composer`). Diffs vs the source JSX:
//   • the source `DaySeparator` grouping is dropped — Message.time is an
//     optional free-form string here, not a guaranteed "day, HH:MM" shape, so
//     splitting on ',' would crash. Day grouping can return when time becomes
//     structured.
//   • the source bottom `Composer` (free-text input + send) is replaced by two
//     explicit shortcut buttons (Голосом / Текстом) — the real input surfaces
//     are the VoiceModal / TextInputModal, opened via onOpenVoice/onOpenText.
//   • exhaustive switch on msg.kind with a `never` default so future kinds
//     (ai_destruct/ai_undo) fail compilation here until handled.

import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { T, Btn, Icon } from '../../tokens';
import { ScreenHeader } from '../../shell';
import { UserBubble } from './UserBubble';
import { AIText } from './AIText';
import { AISummary } from './AISummary';
import { AIClarify } from './AIClarify';
import type { Message, AIClarifyOption } from './Message';

export interface HistoryScreenProps {
  readonly feed: readonly Message[];
  /** Отправка реплики из инлайн-композера (aidepo-5). */
  readonly onSendText: (text: string) => Promise<void>;
  readonly onUndoAction?: (agentActionId: string) => void;
  readonly onClarifyPick?: (msgId: string, option: AIClarifyOption) => void;
  readonly onOpenItem?: (itemId: string) => void;
  readonly onOpenTree?: () => void;
  readonly onBack?: () => void;
}

// Callbacks the per-message dispatch needs. Kept as a narrow bag so the switch
// signature stays readable and only forwards what each bubble consumes.
interface RenderCallbacks {
  readonly onUndoAction?: (agentActionId: string) => void;
  readonly onClarifyPick?: (msgId: string, option: AIClarifyOption) => void;
  readonly onOpenTree?: () => void;
}

function renderMessage(msg: Message, cb: RenderCallbacks): JSX.Element {
  switch (msg.kind) {
    case 'user':
      // exactOptionalPropertyTypes: pass `time` only when defined.
      return msg.time !== undefined ? (
        <UserBubble text={msg.text} time={msg.time} />
      ) : (
        <UserBubble text={msg.text} />
      );
    case 'ai_text':
      return msg.time !== undefined ? (
        <AIText text={msg.text} time={msg.time} />
      ) : (
        <AIText text={msg.text} />
      );
    case 'ai_summary': {
      const handleUndo = cb.onUndoAction;
      const handleOpenTree = cb.onOpenTree;
      // Build props conditionally — optional callbacks must be omitted, not
      // passed as undefined, under exactOptionalPropertyTypes.
      return (
        <AISummary
          msg={msg}
          {...(handleUndo !== undefined ? { onUndo: handleUndo } : {})}
          {...(handleOpenTree !== undefined ? { onOpenTree: handleOpenTree } : {})}
        />
      );
    }
    case 'ai_clarify': {
      const pick = cb.onClarifyPick;
      return (
        <AIClarify
          msg={msg}
          {...(pick !== undefined
            ? { onPick: (opt: AIClarifyOption) => pick(msg.id, opt) }
            : {})}
        />
      );
    }
    default: {
      // Exhaustiveness check — when Phase 1.10/1.11 widen Message with
      // ai_destruct / ai_undo, this assignment fails to compile and forces
      // the next dev to add the missing branches.
      const _exhaustive: never = msg;
      return _exhaustive;
    }
  }
}

function EmptyState(): JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: '40px 32px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 36,
          background: T.brandSoft,
          border: `1px solid ${T.brandSoft2}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="history" size={32} color={T.brandDeep} />
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: T.ink2,
          lineHeight: 1.45,
          maxWidth: 240,
        }}
      >
        Пусто. Скажи или напиши, что положил.
      </div>
    </div>
  );
}

// Инлайн-композер внизу экрана Ассистента (aidepo-5): постоянное поле ввода
// вместо кнопок «Голосом»/«Текстом» и модалки. Голосовой ввод остаётся на
// центральной кнопке нижнего меню.
function Composer({
  onSend,
}: {
  readonly onSend: (text: string) => Promise<void>;
}): JSX.Element {
  const [text, setText] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (): Promise<void> => {
    const trimmed = text.trim();
    if (trimmed.length === 0 || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(trimmed);
      setText('');
    } catch {
      setError('Не удалось отправить — попробуй ещё раз');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: JSX.TargetedKeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <div
      style={{
        padding: '10px 16px 14px',
        background: T.bg,
        borderTop: `1px solid ${T.border}`,
      }}
    >
      {error !== null && (
        <div style={{ fontSize: 12.5, color: T.danger, marginBottom: 8 }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          value={text}
          rows={1}
          disabled={sending}
          onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
          onKeyDown={onKeyDown}
          placeholder="Напиши, что положил…"
          style={{
            flex: 1,
            minHeight: 44,
            maxHeight: 120,
            padding: '11px 14px',
            borderRadius: 14,
            border: `1px solid ${T.border}`,
            fontFamily: T.font,
            fontSize: 15,
            color: T.ink,
            resize: 'none',
            background: T.surface,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <Btn
          kind="primary"
          icon="send"
          onClick={() => void send()}
          disabled={sending || text.trim().length === 0}
        />
      </div>
    </div>
  );
}

export function HistoryScreen({
  feed,
  onSendText,
  onUndoAction,
  onClarifyPick,
  onOpenTree,
  onBack,
}: HistoryScreenProps): JSX.Element {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the newest message whenever the feed grows (or on mount).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [feed.length]);

  const callbacks: RenderCallbacks = {
    ...(onUndoAction !== undefined ? { onUndoAction } : {}),
    ...(onClarifyPick !== undefined ? { onClarifyPick } : {}),
    ...(onOpenTree !== undefined ? { onOpenTree } : {}),
  };

  const isEmpty = feed.length === 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
        fontFamily: T.font,
      }}
    >
      <ScreenHeader
        title="Ассистент"
        subtitle="всё, что я понял и зафиксировал"
        {...(onBack !== undefined ? { onBack } : {})}
      />

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isEmpty ? (
          <EmptyState />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: 16,
            }}
          >
            {feed.map((m) => (
              <div key={m.id}>{renderMessage(m, callbacks)}</div>
            ))}
            {/* Scroll anchor — kept after the last bubble. */}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      <Composer onSend={onSendText} />
    </div>
  );
}
